import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { SeverityBadge } from '@/components/dashboard/severity-badge';
import { CheckCircle2, Clock, Shield, AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Severity, FindingCounts, ScanStatus } from '@nirvik/shared';
import { SCAN_TYPE_LABELS } from '@nirvik/shared';

interface ScanData {
  scanId: string;
  scanType: string;
  status: ScanStatus;
  createdAt: string;
  duration: number | null;
  findingCounts?: FindingCounts;
}

interface FindingData {
  findingId: string;
  scanId: string;
  severity: Severity;
  title: string;
  description: string;
}

const severityOrder: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export function ScanDetailPage() {
  const { orgId, portfolioId, projectId, scanId } = useParams<{ orgId: string; portfolioId: string; projectId: string; scanId: string }>();
  const enabled = !!orgId && !!portfolioId && !!projectId && !!scanId;

  const { data: scanRes, isLoading: scanLoading } = useQuery({
    queryKey: ['scan', orgId, portfolioId, projectId, scanId],
    queryFn: () => apiClient.getScan(orgId!, portfolioId!, projectId!, scanId!),
    enabled,
  });

  const { data: findingsRes, isLoading: findingsLoading } = useQuery({
    queryKey: ['findings', orgId, portfolioId, projectId, { scanId }],
    queryFn: () => apiClient.listFindings(orgId!, portfolioId!, projectId!),
    enabled: !!orgId && !!portfolioId && !!projectId,
  });

  const scan = scanRes?.data as ScanData | undefined;
  const allFindings = (findingsRes?.data as FindingData[] | undefined) || [];
  const findings = allFindings.filter((f) => f.scanId === scanId);
  const isLoading = scanLoading || findingsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-4 bg-gray-100 dark:bg-muted rounded w-32" />
        <Card className="p-4 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-muted rounded w-48 mb-2" />
          <div className="h-4 bg-gray-100 dark:bg-muted rounded w-64" />
        </Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-muted rounded w-12 mx-auto mb-2" />
              <div className="h-4 bg-gray-100 dark:bg-muted rounded-sm w-16 mx-auto" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">Scan not found</h2>
          <p className="text-muted-foreground dark:text-muted-foreground">This scan may have been deleted.</p>
        </div>
      </div>
    );
  }

  const statusConfig: Record<string, { icon: React.ReactNode; bg: string; border: string; titleColor: string; textColor: string; label: string }> = {
    completed: {
      icon: <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400 shrink-0" />,
      bg: 'bg-green-50 dark:bg-green-950/40',
      border: 'border-green-200 dark:border-green-500',
      titleColor: 'text-green-800 dark:text-green-300',
      textColor: 'text-green-600 dark:text-green-400',
      label: 'Scan Completed',
    },
    running: {
      icon: <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 shrink-0 animate-spin" />,
      bg: 'bg-blue-50 dark:bg-blue-950/40',
      border: 'border-blue-200 dark:border-blue-500',
      titleColor: 'text-blue-800 dark:text-blue-300',
      textColor: 'text-blue-600 dark:text-blue-400',
      label: 'Scan Running',
    },
    failed: {
      icon: <XCircle className="h-6 w-6 text-red-600 dark:text-red-400 shrink-0" />,
      bg: 'bg-red-50 dark:bg-red-950/40',
      border: 'border-red-200 dark:border-red-500',
      titleColor: 'text-red-800 dark:text-red-300',
      textColor: 'text-red-600 dark:text-red-400',
      label: 'Scan Failed',
    },
    queued: {
      icon: <Clock className="h-6 w-6 text-muted-foreground dark:text-muted-foreground shrink-0" />,
      bg: 'bg-gray-50 dark:bg-muted/50',
      border: 'border-gray-200 dark:border-border',
      titleColor: 'text-gray-700 dark:text-foreground',
      textColor: 'text-muted-foreground dark:text-muted-foreground',
      label: 'Scan Queued',
    },
    cancelled: {
      icon: <XCircle className="h-6 w-6 text-muted-foreground dark:text-muted-foreground shrink-0" />,
      bg: 'bg-gray-50 dark:bg-muted/50',
      border: 'border-gray-200 dark:border-border',
      titleColor: 'text-gray-700 dark:text-foreground',
      textColor: 'text-muted-foreground dark:text-muted-foreground',
      label: 'Scan Cancelled',
    },
  };

  const sc = statusConfig[scan.status] || statusConfig.completed;
  // Fallback excludes info findings from the total — matches FindingCounts
  // semantics everywhere else in the app.
  const findingCounts = scan.findingCounts || {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
    total: findings.filter((f) => f.severity !== 'info').length,
  };

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <Card className={`p-4 ${sc.bg} ${sc.border}`}>
        <div className="flex items-center gap-3">
          {sc.icon}
          <div>
            <h2 className={`text-sm font-semibold ${sc.titleColor}`}>{sc.label}</h2>
            <p className={`text-xs ${sc.textColor} flex items-center gap-2 mt-0.5`}>
              {scan.duration !== null && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Duration: {formatDuration(scan.duration)}
                </span>
              )}
              {scan.duration !== null && <span>|</span>}
              <span>{SCAN_TYPE_LABELS[scan.scanType as keyof typeof SCAN_TYPE_LABELS] ?? scan.scanType}</span>
            </p>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-foreground">{findingCounts.total ?? findings.length}</p>
          <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">Total Findings</p>
        </Card>
        {severityOrder.map((sev) => (
          <Card key={sev} className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-foreground">{findingCounts[sev] ?? 0}</p>
            <p className="text-xs mt-1">
              <SeverityBadge severity={sev} />
            </p>
          </Card>
        ))}
      </div>

      {/* Findings Preview */}
      <div>
        <h2 className="text-base font-bold text-gray-800 dark:text-foreground uppercase tracking-wide mb-4">Findings</h2>
        {findings.length === 0 ? (
          <p className="text-sm text-muted-foreground dark:text-muted-foreground py-8 text-center">No findings from this scan.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {findings.map((finding) => (
              <Card key={finding.findingId} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <SeverityBadge severity={finding.severity} />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground">{finding.title}</h3>
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground mt-1 line-clamp-2">
                      {finding.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {findings.length > 0 && (
          <div className="mt-4 text-center">
            <Link
              to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            >
              View all findings with details
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
